import { useConexaQuery } from '@/hooks/useConexaQuery'
import services from '@/modules/Attendance/submodules/Scheduled/scheduled-services'
import medicalSpecialtiesServices from '@/modules/MedicalSpecialities/services'
import { toast } from '@conexasaude/conexa-hero'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FIVE_MINUTES_IN_MS } from './constants'
import {
  CanRedirectToNewScheduleParams,
  ProfessionalsBodyParams,
  ProfessionalScheduleParams,
  ProfessionalAvailabilityParams,
  CheckProfessionalSlotsParams,
} from './scheduled-types'
import { ConexaGenericError } from '@/services/httpClient'

export const useMedicProfile = (id: string | number) => {
  return useConexaQuery({
    queryKey: ['get-medic-profile', id],
    service: () => services.getMedicProfile(String(id)),
    config: {
      enabled: Boolean(id),
      refetchOnWindowFocus: false,
    },
  })
}

export const useSendProfessionalWarning = (id: string | number) => {
  return useConexaQuery({
    queryKey: ['get-professional-warning', id],
    service: () => services.sendProfessionalWarning(String(id)),
    config: {
      enabled: false,
      refetchOnWindowFocus: false,
    },
  })
}

export const useHealthInsuranceCards = (enabled: boolean) => {
  return useConexaQuery({
    queryKey: ['get-health-insurance-cards'],
    service: () => services.getHealthInsuranceCards(),
    config: {
      refetchOnWindowFocus: false,
      enabled,
    },
  })
}

export const useProfessionalProfileTimeSlot = (
  professionalId: string | number,
) => {
  return useConexaQuery({
    queryKey: ['get-professional-profile-time-slot'],
    service: () => services.getProfessionalProfileTimeSlot(professionalId),
    config: {
      enabled: !!professionalId,
      refetchOnWindowFocus: false,
    },
  })
}

export const useMedicalSpecialities = () => {
  return useConexaQuery({
    queryKey: ['get-medical-specialities'],
    service: () => services.getMedicalSpecialties(),
    config: {
      refetchOnWindowFocus: false,
      cacheTime: FIVE_MINUTES_IN_MS,
      staleTime: FIVE_MINUTES_IN_MS,
    },
  })
}

export const useSpecialitiesAndCouncilsList = (enabled = true) => {
  return useConexaQuery({
    queryKey: ['get-specialities-and-councils-scheduled'],
    service: () => services.getSpecialitiesAndCouncils(),
    config: {
      enabled,
      staleTime: FIVE_MINUTES_IN_MS,
      cacheTime: FIVE_MINUTES_IN_MS,
      refetchOnWindowFocus: false,
    },
  })
}

export const useGetQueueSize = (clinicId: number) => {
  return useConexaQuery({
    queryKey: ['get-queue-size', `get-queue-size-${clinicId}`],
    service: () => medicalSpecialtiesServices.getQueueSize(clinicId),
    config: {
      refetchOnWindowFocus: false,
      cacheTime: FIVE_MINUTES_IN_MS,
      staleTime: FIVE_MINUTES_IN_MS,
    },
  })
}

export const useRequestImmediateAttendance = () => {
  return useMutation({
    mutationFn: services.requestImmediateAttendance,
    mutationKey: ['request-immediate-attendance'],
    retry: false,
    onError: (error: AxiosError) => {
      toast({
        variant: 'error',
        message:
          error?.response?.data?.msg ||
          'Tente novamente mais tarde, ou agende em um horário próximo.',
      })
    },
  })
}

export const useCanRedirectToNewSchedule = () => {
  return useMutation({
    mutationFn: (params: CanRedirectToNewScheduleParams) =>
      services.canRedirectToNewSchedule(params),
    mutationKey: ['can-redirect-to-new-schedule'],
    retry: false,
    onError: (error: ConexaGenericError) => {
      toast({
        variant: 'error',
        message:
          error?.response?.data?.msg ||
          'Não foi possível verificar o redirecionamento para agenda por slot.',
      })
    },
  })
}

export const useProfessionals = (params: ProfessionalsBodyParams) => {
  return useInfiniteQuery({
    queryKey: ['get-professionals', { ...params }],
    queryFn: async ({ pageParam = 1 }) => {
      return await services.getProfessionals({
        ...params,
        page: pageParam,
      })
    },
    getNextPageParam: (lastPage, pages) => {
      const total = lastPage?.total
      const allItems = pages?.map(item => item?.items)?.flat()

      if (total > allItems?.length) {
        return pages?.length + 1
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export const useCheckProfessionalAvailability = ({
  professionalId,
  enabled,
}: ProfessionalAvailabilityParams) => {
  return useConexaQuery({
    queryKey: ['check-professional-availability', professionalId],
    service: () => services.checkProfessionalAvailability(professionalId),
    config: {
      enabled: enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
    },
  })
}

export const useProfessionalSchedule = (params: ProfessionalScheduleParams) => {
  const { professionalId, startDate, endDate } = params

  return useConexaQuery({
    queryKey: ['get-psychologist-days-v2', professionalId, startDate, endDate],
    service: () => services.getProfessionalSchedule(params),
    config: {
      enabled:
        Boolean(professionalId) && Boolean(startDate) && Boolean(endDate),
      refetchOnWindowFocus: false,
      retry: 1,
    },
  })
}

export const useGetProfessionalIsNotified = ({
  professionalId,
  enabled,
}: ProfessionalAvailabilityParams) => {
  return useConexaQuery({
    queryKey: ['get-professional-is-notified', professionalId],
    service: () => services.getProfessionalIsNotified(professionalId),
    config: {
      enabled: enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
    },
  })
}

export const useRequestScheduleNotification = () => {
  return useMutation({
    mutationFn: (professionalId: number) =>
      services.requestScheduleNotification(professionalId),
    onSuccess: () => {
      toast({
        message: 'Te avisaremos assim que abrirem novos horários!',
      })
    },
    onError: (error: AxiosError) => {
      toast({
        message:
          error?.response?.data?.msg ??
          'Ocorreu um erro ao solicitar a notificação de novos horários.',
        variant: 'error',
      })
    },
  })
}

export const useCheckProfessionalSlots = () => {
  return useMutation({
    mutationFn: async ({
      slotId,
      professionalId,
      dateAndHour,
    }: CheckProfessionalSlotsParams) => {
      return await services.checkProfessionalSlots({
        slotId,
        professionalId,
        dateAndHour,
      })
    },
  })
}
